import { VirtualizedDataTable } from '@/components/dataTable/VirtualizedDataTable'
import MetricDialog from '@/components/metricsComponents/metricDialog'
import { columns, Metric } from '@/components/metricsComponents/metricsColumns'
import { Button } from '@/components/ui/button'
import DeleteAlert from '@/components/DeleteAlert'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { RxPlus } from 'react-icons/rx'
import { trpc } from '@/lib/trpc/client'
import {
  useHasPermission,
  useOrg,
  useUserId,
} from '@/lib/states/session/hooks'
import { errorToast } from '@/lib/utils/ToastUtils'
import { patchListCache, removeFromList } from '@trainrr/api-client'
import { canDeleteOrgLibraryItem } from '@/lib/libraryAccess'

export const Route = createFileRoute('/_auth/_dashboard/metrics')({
  component: MetricsPage,
})

function MetricsPage() {
  const org = useOrg()
  const userId = useUserId()
  const canManageLibrary = useHasPermission('library.edit')
  const [open, setOpen] = useState(false)
  const [metric, setMetric] = useState<Metric>()
  const [metricToDelete, setMetricToDelete] = useState<Metric>()
  const weightUnitLabel = org?.weightUnitDefault === 'lbs' ? 'lbs' : 'kg'
  const tableColumns = useMemo(
    () =>
      columns({
        hideEditButton: false,
        weightUnitLabel,
        userId,
        canManageLibrary,
      }),
    [weightUnitLabel, userId, canManageLibrary],
  )

  const {
    data: metrics,
    error,
    isLoading,
  } = trpc.metrics.list.useQuery(undefined, {
    enabled: Boolean(org?.id),
  })
  const utils = trpc.useUtils()
  const deleteMetric = trpc.metrics.delete.useMutation({
    onSuccess: (_data, variables) => {
      patchListCache(utils.metrics.list, (old) =>
        removeFromList(old, variables.id),
      )
      setMetricToDelete(undefined)
    },
    onError: (mutationError) => {
      errorToast(mutationError)
    },
  })

  if (error) {
    console.error('error fetching metrics', error)
  }

  const handleOnClickEdit = (selected: Metric) => {
    if (open) {
      return
    }
    setMetric(selected)
    setOpen(true)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setMetric(undefined)
    setOpen(nextOpen)
  }

  const handleMetricDeleted = (selected: Metric) => {
    if (!canDeleteOrgLibraryItem(selected, { userId, canManageLibrary })) {
      return
    }
    setMetricToDelete(selected)
  }

  return (
    <>
      <DeleteAlert
        title={`Delete ${metricToDelete?.name ?? 'Metric'}`}
        description="Are you sure you want to delete this metric? This action cannot be undone."
        open={metricToDelete !== undefined}
        onCancel={() => setMetricToDelete(undefined)}
        onDelete={() => {
          if (
            !metricToDelete?.id ||
            !canDeleteOrgLibraryItem(metricToDelete, {
              userId,
              canManageLibrary,
            })
          ) {
            setMetricToDelete(undefined)
            return
          }
          deleteMetric.mutate({ id: metricToDelete.id })
        }}
      />
      <MetricDialog
        open={open}
        onOpenChange={handleOpenChange}
        metric={metric}
      />
      <div className="flex h-full min-h-0 w-full flex-col">
        <div className="flex h-full min-h-0 w-full flex-col md:flex">
          <div className="flex min-h-0 flex-1 flex-col gap-4 p-8 pt-6">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-3xl font-bold tracking-tight">Metrics</h2>
              <Button className="gap-2" onClick={() => setOpen(true)}>
                <RxPlus /> Add New
              </Button>
            </div>
            <VirtualizedDataTable
              className="min-h-0 flex-1"
              columns={tableColumns}
              data={metrics ?? []}
              onRowSelected={handleOnClickEdit}
              onRowDeleted={handleMetricDeleted}
              stickyHeader
              isLoading={isLoading}
              tableContainerClassName="min-h-0 flex-1"
              tableWrapperClassName="h-full min-h-0"
              virtualizedHeight="100%"
              estimateRowHeight={72}
            />
          </div>
        </div>
      </div>
    </>
  )
}
